import { apiClient } from '@/shared/api/client';

/**
 * Fetch a paginated and filtered list of products.
 * @param {Object} params - Query params (search, brand, category, limit, offset)
 * @returns {Promise<Object>}
 */
export async function getProducts(params = {}) {
  const response = await apiClient.get('/api/v1/products', { params });
  return response.data;
}

/**
 * Fetch a single product by its slug.
 * @param {string} slug - The unique product slug
 * @returns {Promise<Object>}
 */
export async function getProductBySlug(slug) {
  const response = await apiClient.get(`/api/v1/products/slug/${slug}`);
  return response.data;
}

/**
 * Fetch the hierarchical list of all product categories.
 * @returns {Promise<Array>}
 */
export async function getCategories() {
  const response = await apiClient.get('/api/v1/products/categories');
  return response.data;
}

/**
 * Submit a request for quote.
 * @param {Object} quoteData - User details and product identification
 * @returns {Promise<Object>}
 */
export async function requestQuote(quoteData) {
  const response = await apiClient.post('/api/v1/products/quote', quoteData);
  return response.data;
}

/**
 * Fetch grouped partner brands and their top products for the store landing page.
 * @returns {Promise<Array>}
 */
export async function getStoreLandingData() {
  const response = await apiClient.get('/api/v1/products/landing-data');
  return response.data;
}

/**
 * Fetch a single brand's metadata by its slug.
 * @param {string} slug - Unique brand slug URL
 * @returns {Promise<Object>}
 */
export async function getBrandBySlug(slug) {
  const response = await apiClient.get(`/api/v1/products/brands/slug/${slug}`);
  return response.data;
}

/**
 * Fetch list of all brand names.
 * @returns {Promise<Array<string>>}
 */
export async function getBrands() {
  const response = await apiClient.get('/api/v1/products/brands');
  return response.data;
}

/**
 * Perform instant global quick-search across brands, categories, and products.
 * @param {string} query - Search query
 * @param {number} limit - Max results per group
 * @returns {Promise<{brands: Array, categories: Array, products: Array}>}
 */
export async function quickSearch(query, limit = 6) {
  if (!query || !query.trim()) {
    return { brands: [], categories: [], products: [] };
  }
  const response = await apiClient.get('/api/v1/products/quick-search', {
    params: { q: query.trim(), limit }
  });
  return response.data;
}

